import { PropsWithChildren, useEffect, useRef } from 'react';
import { animate, AnimatePresence, motion, useMotionValue } from 'motion/react';
import {
  resolveValue,
  Toast as IToast,
  Toaster as HotToaster,
} from 'react-hot-toast';

import { easeOutSmooth } from '@components/primitives/utilities';

import { Toast, ToastProps } from './Toast';

import css from './styles.module.css';

interface BounceOnChangeProps extends PropsWithChildren {
  toast: IToast;
}

function BounceOnChange({ toast, children }: BounceOnChangeProps) {
  const wasLoadingRef = useRef(toast.type === 'loading');

  const previousRef = useRef(toast.createdAt);

  const scale = useMotionValue(1);

  useEffect(() => {
    if (previousRef.current !== toast.createdAt) {
      previousRef.current = toast.createdAt;

      if (wasLoadingRef.current) return;

      animate(scale, 1.04, {
        duration: 0.1,
        ease: 'easeOut',
        onComplete: () => {
          animate(scale, 1, {
            duration: 0.1,
            ease: 'easeOut',
          });
        },
      });
    }
  }, [toast.createdAt]);

  return (
    <motion.div className={css.bounce} style={{ scale }}>
      {children}
    </motion.div>
  );
}

export function Toaster() {
  return (
    <div data-testid="toaster">
      <HotToaster
        position="bottom-center"
        containerStyle={{ bottom: 16 }}
        gutter={8}
      >
        {(toast) => {
          let variant: ToastProps['variant'] = 'info';

          if (toast.className?.split(' ').includes('warning')) {
            variant = 'warning';
          } else if (toast.type !== 'blank' && toast.type !== 'custom') {
            variant = toast.type;
          }

          return (
            <AnimatePresence>
              {toast.visible && (
                <motion.div
                  key={toast.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      opacity: { duration: 0.4, ease: easeOutSmooth },
                      y: { duration: 0.3, ease: easeOutSmooth },
                    },
                  }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.3, ease: easeOutSmooth }}
                >
                  <BounceOnChange toast={toast}>
                    <Toast variant={variant}>
                      {resolveValue(toast.message, toast)}
                    </Toast>
                  </BounceOnChange>
                </motion.div>
              )}
            </AnimatePresence>
          );
        }}
      </HotToaster>
    </div>
  );
}
